/**
 * Pin capture registry (F5).
 *
 * The workspace store lags Monaco/tab state by ~200ms, so pinning a
 * workspace must pull the LIVE definition straight from the owning tab
 * component at click time rather than from the store. Tab components
 * register a capture callback keyed by `tabId`; the tab-strip context menu
 * (a different component tree, with no direct reference to the tab
 * component) calls `capturePinDefinition(tabId)` to read it.
 *
 * This is a plain module-level `Map`, not a Zustand store or React context.
 * It IS observable, though (Codex gate-3 F3-followup): tab components
 * register their capture from a `useEffect`, which runs AFTER first render,
 * so a freshly-mounted tab briefly has no registration. `useWorkspacePinState`
 * derives eligibility from `capturePinDefinition`, and subscribes to this
 * registry's version counter via `subscribePinCaptures`/`getPinCaptureVersion`
 * so that registration/unregistration deterministically triggers a re-render
 * instead of leaving a scratch tab's pin affordance stuck on `disabled` until
 * some unrelated state change happens to re-render it.
 */

import type { CapturedDefinition } from '../../../database-objects/services/pinnedObjects/pinDivergence';

type PinCaptureFn = () => CapturedDefinition | null;

const captures = new Map<string, PinCaptureFn>();

let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version += 1;
  listeners.forEach((l) => l());
}

/**
 * Registers the live capture callback for `tabId`. Returns an `unregister`
 * function that removes ONLY this registration — if `tabId` was
 * re-registered with a new callback in the meantime, an earlier unregister
 * is a no-op so it can't clobber the newer registration.
 */
export function registerPinCapture(tabId: string, fn: PinCaptureFn): () => void {
  captures.set(tabId, fn);
  notify();
  return () => {
    if (captures.get(tabId) === fn) {
      captures.delete(tabId);
      notify();
    }
  };
}

/**
 * Subscribes to registry changes (register/unregister). Returns a remover.
 * Intended for `useSyncExternalStore` consumers (e.g. `useWorkspacePinState`)
 * that need to recompute eligibility when a capture appears or disappears.
 */
export function subscribePinCaptures(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The registry's monotonically-incrementing version counter — bumped on
 * every register/unregister. A stable `getSnapshot` for `useSyncExternalStore`.
 */
export function getPinCaptureVersion(): number {
  return version;
}

/**
 * Captures the live definition for `tabId`, or `null` when it can't —
 * either no capture is registered (unknown tab) or the registered callback
 * threw (stale Monaco ref, serialization failure, …; logged, never
 * propagated). Callers treat `null` as "not pinnable right now".
 */
export function capturePinDefinition(tabId: string): CapturedDefinition | null {
  const fn = captures.get(tabId);
  if (!fn) return null;
  try {
    return fn();
  } catch (err) {
    console.error('[pinCaptureRegistry] capture callback threw:', err);
    return null;
  }
}
